<template>
  <div class="date-window modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        Profile Date
      </p>
      <a class="close-modal" aria-label="close" @click="closeModal()" v-if="!asOfError">
        <i class="material-icons">close</i>
      </a>
    </header>
    <section class="as-of-error-section" v-if="asOfError">
      <div class="as-of-alert-error">The date specified in your profile is no longer valid. Please select a new date to continue, or click cancel to abort the loading of the profile.</div>
    </section>
    <section>
      <div class="left-side" :class="{ 'focused': focusedSide === FocusedSide.Left }" @click="focusedSide = FocusedSide.Left">
        <h5>Choose a specific date</h5>

        <div class="calendar-container">
          <v-date-picker :is-inline="true" mode="single"
            v-model="date"
            :from-page="manualMonthToShow"
            :max-date='maxDate'
            :min-date='minDate'
            @input="datePickerUpdated($event)"
            ref="calendar"
          />
        </div>

      </div>
      <div class="right-side" :class="{ 'focused': focusedSide === FocusedSide.Right }">
        <div class="input-area" @click="focusedSide = FocusedSide.Right">
          <h5>Choose a relative date(?)</h5>
          <div class="input-row">
            <input class="input" min="0" step="1" type="number" v-model.number="offset" />
            <div class="select">
              <select class="select" v-model="offsetUnit">
                <option :value="unit" v-for="unit in offsetUnitList" :key="unit">{{ unit }}</option>
              </select>
            </div>
            <div class="postfix"><span :class="{ 'hidden': offset === 1 }">s</span> ago</div>
          </div>

          <span class="error-text" v-if="relativeValueError && focusedSide === FocusedSide.Right">
            {{ relativeValueError }}
          </span>
        </div>
        <div class="button-area">
          <button class="button button-secondary button-outline button-medium"
            @click="cancel()"
          >
            Cancel
          </button>
          <button class="button button-primary button-medium save-button"
            :disabled="(relativeValueError && focusedSide === FocusedSide.Right) || (!date  && focusedSide === FocusedSide.Left)"
            @click="save()"
          >
            Apply
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { AsOf, AsOfType, AsOfData, AsOfRelativeUnit } from "../../store/types";
import utilsService from '../../services/utils.service';
import moment from 'moment';

enum FocusedSide { Left, Right }
type MomentOffsetUnit = 'days' | 'weeks' | 'months' | 'years';

@Component({
  components: {
  }
})
export default class DateSelectModal extends Vue {

  public FocusedSide = FocusedSide;

  get asOfError(): string | undefined {
    return this.$store.state.profiles.asOfError;
  }

  get maxDate() {
    return this.$store.state.options.maxAsOf ? new Date(this.$store.state.options.maxAsOf) : new Date();
  }
  get minDate() {
    return this.$store.state.options.minAsOf ? new Date(this.$store.state.options.minAsOf) : null;
  }
  get minDateAsOffsetValue() {
    if (this.minDate) {
      const now = moment();
      let momentType: MomentOffsetUnit = this.offsetUnit + 's' as MomentOffsetUnit;
      return now.diff(this.minDate, momentType);
    }

    return null;
  }
  get maxDateAsOffsetValue() {
    if (this.maxDate) {
      const now = moment();
      let momentType: MomentOffsetUnit = this.offsetUnit + 's' as MomentOffsetUnit;
      return now.diff(this.maxDate, momentType) + 1;
    }

    return null;
  }

  get relativeValueError() {
    const asOf = {
      type: AsOfType.Relative,
      data: { count: this.offset, unit: this.offsetUnit }
    };

    const date = utilsService.convertAsOfToDate(asOf);
    const time = date.getTime();

    const min = this.$store.getters.getMinTime;
    const max = this.$store.getters.getMaxTime;
    const asOfError = utilsService.isDateOutsideRange(time, min, max);

    if (asOfError) {
      return `The relative date selected must be between ${this.maxDateAsOffsetValue} and ${this.minDateAsOffsetValue} ${this.offsetUnit}s ago (inclusive).`;
    }

    return null;
  }

  public date: Date | null = null;
  public offset: number = 0;
  public offsetUnitList: AsOfRelativeUnit[] = Object.values(AsOfRelativeUnit);
  public offsetUnit: AsOfRelativeUnit = AsOfRelativeUnit.Day;
  public focusedSide: FocusedSide = FocusedSide.Right;
  public manualMonthToShow: any = {};

  public cancel() {
    if (this.asOfError) {
      this.$store.commit('setAsOfError');
      this.$store.dispatch('clearSelections');
      this.$store.commit('setisLoadingFromProfile', false);
    }

    this.closeModal();
  }

  public closeModal() {
    this.$store.commit('setModal', '');
  }

  public save() {
    let asOf: AsOf;

    if (this.focusedSide === FocusedSide.Left) {
      const date = this.date ? new Date(this.date.getTime() - this.date.getTimezoneOffset() * 60000) : new Date();
      asOf = { type: AsOfType.Static, data: date.toISOString().slice(0,10) };
    } else {
      asOf = {
        type: AsOfType.Relative,
        data: { count: this.offset, unit: this.offsetUnit }
      };
    }

    if (this.asOfError) {
      this.$store.commit('setAsOfError');
      this.$store.commit('setisLoadingFromProfile', false);
    }
    this.$store.commit('setAsOf', asOf); 
    this.closeModal();
  }

  public datePickerUpdated(e: any) {
    this.date = e;
  }

  public mounted() {
    const asOf: AsOf = this.$store.getters.getAsOf;

    switch (asOf.type) {
      case AsOfType.Relative:
        const { count, unit } = asOf.data as AsOfData;
        this.offset = count;
        this.offsetUnit = unit;
        this.focusedSide = FocusedSide.Right;
        break;

      case AsOfType.Static:
        this.focusedSide = FocusedSide.Left;
        const date = new Date(asOf.data as string);
        this.manualMonthToShow = {
          month: date.getMonth() + 1,
          year: date.getFullYear()
        }
        const calendar = this.$refs.calendar as any;
        calendar.adjustPageRange();

        if (!this.asOfError) {
          this.date = date;
        }

        break;
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/partials/_settings.colors.scss";
.modal .date-window {
  min-width: 700px;

  h5 {
    text-align: center;
    margin-bottom: 14px;
  }

  section.as-of-error-section {
    border-bottom: 1px solid $border-color;
    border-radius: 0;
    padding: 10px;

    .as-of-alert-error {
      margin-top: 0;
      text-align: left;
      background-color: rgba(255, 204, 128, .45);
      border: 2px solid rgba(255, 204, 128, 1);
      padding: 3px 5px;
      border-radius: 4px;
      width: 100%;

      i {
        position: relative;
        top: 4px;
        color: #d89d44;
      }
    }
  }


  section {
    display: flex;
    padding: 0;

    %not-focused {
      opacity: 0.3;
    }

    .left-side,
    .right-side {
      padding: 20px;
      width: 50%;
    }

    .left-side {
      .calendar-container {
        display: flex;
        justify-content: center;
      }

      &:not(.focused) {
        .calendar-container {
          @extend %not-focused;
        }
      }
    }

    .right-side {
      border-left: 1px solid $border-color;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      &:not(.focused) {
        .input-row, .error-text {
          @extend %not-focused;
        }
      }

      .input-row {
        align-items: center;
        display: flex;
        justify-content: space-around;

        .input {
          min-width: 0;
        }

        > .select {
          margin: {
            left: 0.5em;
            right: 0.5em;
          }
        }

        .postfix {
          white-space: nowrap;

          .hidden {
            visibility: hidden;
          }
        }
      }

      .error-text {
        font-size: 12px;
        color: $red;
        text-align: left;
        font-weight: 500;
        margin-top: 15px;
        display: block;
      }

      .button-area {
        display: flex;
        justify-content: flex-end;

        .save-button {
          margin-left: 10px;
        }
      }
    }
  }
}
</style>
